import React from 'react';

interface FeatureGroupProps {
  /** Small uppercase label above the headline (e.g., "Practice") */
  eyebrow: string;
  /** Group headline (h2-scale) */
  headline: React.ReactNode;
  /** Optional supporting line beneath the headline */
  subline?: React.ReactNode;
  /** Anchor id for in-page navigation (e.g., "practice") */
  id?: string;
  /**
   * Background variant — drives the page's light/dark alternation.
   * 'blue' = blue-50 / blue-950, 'neutral' = white / gray-950.
   */
  bg?: 'blue' | 'neutral';
  /** Compact rows (or a single band) belonging to this group */
  children: React.ReactNode;
  className?: string;
}

const BG_CLASSES: Record<NonNullable<FeatureGroupProps['bg']>, string> = {
  blue: 'bg-blue-50 dark:bg-blue-950',
  neutral: 'bg-white dark:bg-gray-950',
};

/**
 * FeatureGroup — a grouped, nav-mirrored section wrapper for the homepage
 * feature middle. Renders an eyebrow + headline + optional subline header,
 * then its compact rows. One <section> per group counts as a single section
 * for the page-wide background alternation.
 */
export function FeatureGroup({
  eyebrow,
  headline,
  subline,
  id,
  bg = 'neutral',
  children,
  className = '',
}: FeatureGroupProps) {
  return (
    <section
      id={id}
      className={`py-14 md:py-16 ${BG_CLASSES[bg]} overflow-x-hidden ${className}`}
      style={{ scrollMarginTop: '6rem' }}
    >
      <div className="max-w-7xl mx-auto px-4 box-border">
        {/* Group header */}
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-12">
          <span className="inline-block text-sm font-semibold uppercase tracking-wide text-[#F37022] mb-3">
            {eyebrow}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-black dark:text-white mb-4">
            {headline}
          </h2>
          {subline && (
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
              {subline}
            </p>
          )}
        </div>

        {/* Group body — compact rows */}
        <div className="space-y-2 md:space-y-4">{children}</div>
      </div>
    </section>
  );
}
